import { isSSR } from '@k2/core/util'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useApiLoad, useApiRequest } from '@/api'
import { type Concept, type ConceptType, useConceptsResource } from '@/deliverables/resources'

export const useConceptStore = defineStore('concept', () => {
  const { getConcepts, setConcept, setConceptDuplicate, delConcept } = useConceptsResource()
  const { run, runScoped, loading: acting, failure: actionFailure } = useApiRequest()
  const list = useApiLoad()
  const loading = computed(() => list.loading.value || acting.value)
  const failure = computed(() => list.failure.value ?? actionFailure.value)

  const concepts = ref<Record<string, Concept[]>>({})
  let shownCampaignId: string | undefined

  const store = (campaignId: string, rows: Concept[] | undefined) => {
    if (rows) concepts.value = { ...concepts.value, [campaignId]: rows }
  }

  const load = async (organizationId: string, campaignId: string) => {
    if (isSSR) return
    shownCampaignId = campaignId
    actionFailure.value = undefined
    store(campaignId, await list.run<Concept[]>(() => getConcepts(organizationId, campaignId)))
  }

  const refresh = async (organizationId: string, campaignId: string) => {
    if (campaignId === shownCampaignId) return load(organizationId, campaignId)
    store(campaignId, await runScoped<Concept[]>(() => getConcepts(organizationId, campaignId)).result)
  }

  const createByHand = async (organizationId: string, campaignId: string, type: ConceptType) => {
    const created = await run<{ id: string }>(() => setConcept(organizationId, campaignId, type))
    if (created) await refresh(organizationId, campaignId)
    return created?.id
  }

  const duplicate = async (organizationId: string, campaignId: string, conceptId: string) => {
    if (await run<{ id: string }>(() => setConceptDuplicate(organizationId, conceptId))) await refresh(organizationId, campaignId)
  }

  const remove = async (organizationId: string, campaignId: string, conceptId: string) => {
    await run(() => delConcept(organizationId, conceptId))
    if (!actionFailure.value) await refresh(organizationId, campaignId)
  }

  return { concepts, loading, failure, load, createByHand, duplicate, remove }
})
